import type { publicOperatorAdmin } from "@kintone-storage/openapi-types";
import { organizationScopedSessionStorageKey } from "@/lib/organization-scope";

const IDEMPOTENCY_KEY = /^[A-Za-z0-9._:-]{1,128}$/u;

type OperatorOperation = publicOperatorAdmin.operations;
type OperationHeaders<T extends keyof OperatorOperation> = NonNullable<
  OperatorOperation[T]["parameters"]["header"]
>;
type WithRequiredIdempotencyKey<T extends { "Idempotency-Key"?: string }> =
  Pick<T, "Idempotency-Key"> extends Required<
    Pick<T, "Idempotency-Key">
  > ? T : never;
type RequiredIdempotencyHeaders =
  & WithRequiredIdempotencyKey<OperationHeaders<"createOperatorStorageRegistration">>
  & WithRequiredIdempotencyKey<OperationHeaders<"completeOperatorStorageBrowseSelection">>
  & WithRequiredIdempotencyKey<OperationHeaders<"deleteOperatorStorage">>
  & WithRequiredIdempotencyKey<OperationHeaders<"issueOperatorLocalGatewaySetupHandoff">>
  & WithRequiredIdempotencyKey<OperationHeaders<"issueOperatorLocalGatewayRouteHandoff">>;

export type PendingCommand = {
  fingerprint: string;
  idempotencyKey: string;
};

function availableSessionStorage(): Storage | null {
  if (typeof sessionStorage === "undefined") {
    return null;
  }
  return sessionStorage;
}

export function readPendingCommand(
  sessionKey: string,
  organizationScopeId?: string | null,
): PendingCommand | null {
  try {
    const storage = availableSessionStorage();
    const scopedSessionKey = organizationScopedSessionStorageKey(
      sessionKey,
      organizationScopeId,
    );
    if (!scopedSessionKey) return null;
    const raw = storage?.getItem(scopedSessionKey);
    if (!raw) {
      return null;
    }
    const value = JSON.parse(raw) as Partial<PendingCommand>;
    if (
      typeof value.fingerprint !== "string" ||
      value.fingerprint.length === 0 ||
      typeof value.idempotencyKey !== "string" ||
      !IDEMPOTENCY_KEY.test(value.idempotencyKey)
    ) {
      storage?.removeItem(scopedSessionKey);
      return null;
    }
    return {
      fingerprint: value.fingerprint,
      idempotencyKey: value.idempotencyKey,
    };
  } catch {
    return null;
  }
}

export function writePendingCommand(
  sessionKey: string,
  command: PendingCommand,
  organizationScopeId?: string | null,
) {
  try {
    const scopedSessionKey = organizationScopedSessionStorageKey(
      sessionKey,
      organizationScopeId,
    );
    if (!scopedSessionKey) return;
    availableSessionStorage()?.setItem(scopedSessionKey, JSON.stringify(command));
  } catch {
    // The mounted caller still retains the identity in memory.
  }
}

export function clearPendingCommand(
  sessionKey: string,
  organizationScopeId?: string | null,
) {
  try {
    const scopedSessionKey = organizationScopedSessionStorageKey(
      sessionKey,
      organizationScopeId,
    );
    if (!scopedSessionKey) return;
    availableSessionStorage()?.removeItem(scopedSessionKey);
  } catch {
    // A terminal response remains authoritative when browser cleanup fails.
  }
}

export function idempotencyKeyForCommand(
  ref: { current: PendingCommand | null },
  body: Record<string, unknown>,
  sessionKey?: string,
  organizationScopeId?: string | null,
) {
  const fingerprint = JSON.stringify(body);
  if (ref.current?.fingerprint !== fingerprint) {
    ref.current = { fingerprint, idempotencyKey: crypto.randomUUID() };
  }
  if (sessionKey) {
    writePendingCommand(sessionKey, ref.current, organizationScopeId);
  }
  return ref.current.idempotencyKey;
}

export function requiredIdempotencyHeaders(idempotencyKey: string) {
  return {
    "Idempotency-Key": idempotencyKey,
  } satisfies RequiredIdempotencyHeaders;
}
